import { css } from '@emotion/react';
import { PaymentPlanHistoryItem } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { getPaymentMethodName } from '@frontend/payments-card-on-file';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { MarkIcon, Modal, RefundIcon, Text, TextButton, UnmarkIcon } from '@frontend/design-system';
import { formatCentsToCurrency } from '../../utils/currency-helpers';
import { isoDateToDisplayLocalString } from '../../utils/date-helpers';
import { isPlanScheduled } from '../../utils/payment-plan-helpers';
import { PaymentPlanInvoiceStatusChip } from '../PaymentPlanDetails/PaymentPlanInvoiceStatusChip';

export const ViewPaymentDetailsModal = ({
  paymentDetails,
  onClose,
  toggleRecorded,
  isRecorded,
}: {
  paymentDetails: PaymentPlanHistoryItem;
  onClose: () => void;
  toggleRecorded: (data: PaymentPlanHistoryItem, recordedAt: string | undefined, paymentId: string | undefined) => void;
  isRecorded: boolean;
}) => {
  const { t } = useTranslation('payments');
  const navigate = useNavigate();
  const paymentSummary = [
    {
      key: 'dateDue',
      title: t('Due Date'),
      value: isoDateToDisplayLocalString(paymentDetails.payment?.scheduledFor || ''),
    },
    {
      key: 'datePaid',
      title: paymentDetails.payment?.paidAt ? t('Date Paid') : t('Date Scheduled'),
      value: isoDateToDisplayLocalString(paymentDetails.payment?.paidAt || paymentDetails.payment?.scheduledFor || ''),
    },
    {
      key: 'status',
      title: t('Payment Status'),
      value: paymentDetails.payment?.status,
    },
    {
      key: 'amountPaid',
      title: t('Payment Amount'),
      value: formatCentsToCurrency(paymentDetails.payment?.amount),
    },
    {
      key: 'remainingAmount',
      title: t('Remaining Balance'),
      value: formatCentsToCurrency(paymentDetails.payment?.balanceRemaining),
    },
    {
      key: 'remainingTerm',
      title: t('Payments Remaining'),
      value: paymentDetails.payment?.remainingTerm,
    },
    {
      key: 'billingMethod',
      title: t('Billing method'),
      value: getPaymentMethodName(paymentDetails.payment?.paymentMethod),
    },
  ];

  return (
    <>
      <Modal.Header>
        <div
          css={css`
            display: flex;
            flex-flow: row;
            justify-content: space-between;
            align-items: center;
          `}
        >
          {t('Payment Details')}
          {!isPlanScheduled(paymentDetails) && (
            <div
              css={css`
                display: flex;
                flex-flow: row;
                align-items: center;
              `}
            >
              <TextButton
                icon={isRecorded ? UnmarkIcon : MarkIcon}
                onClick={() =>
                  toggleRecorded(paymentDetails, paymentDetails.payment?.recordedAt, paymentDetails.payment?.paymentId)
                }
                data-trackingid={`payment-plan-paymentdetail-btn-${isRecorded ? 'unrecorded' : 'recorded'}`}
              >
                {isRecorded ? t('Mark Unrecorded') : t('Mark Recorded')}
              </TextButton>
              {!paymentDetails.payment?.hasRefund && (
                <TextButton
                  onClick={() =>
                    paymentDetails?.payment?.id
                      ? navigate({
                          to: '/payments/invoices/$id',
                          params: { id: paymentDetails.payment?.id },
                        })
                      : navigate({
                          to: '/payments/invoices',
                        })
                  }
                  icon={RefundIcon}
                  data-trackingid={'payment-plan-paymentdetail-btn-refund'}
                >
                  {t('Refund')}
                </TextButton>
              )}
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
            margin-top: ${theme.spacing(2)};
          `}
        >
          {paymentSummary.map(({ key, title, value }) => (
            <div
              key={key}
              id={key}
              css={css`
                display: flex;
                flex-flow: row nowrap;
                align-items: center;
                gap: ${theme.spacing(2)};
              `}
            >
              <Text
                weight='semibold'
                css={css`
                  width: 160px;
                  color: ${theme.colors.neutral50};
                `}
              >
                {title}
              </Text>
              {key === 'status' ? (
                <PaymentPlanInvoiceStatusChip paymentPlanInvoiceStatus={paymentDetails.payment?.status} />
              ) : (
                <Text>{value}</Text>
              )}
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Actions
        css={{ button: { width: 'fit-content' }, justifyContent: 'end' }}
        onPrimaryClick={onClose}
        primaryLabel={t('Done')}
      />
    </>
  );
};
